import React from 'react';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// The Ad Library search these keyword settings would run, as a link.
//
// Worth showing on the form rather than only after a fetch, because the filters
// fail quietly: a keyword that targets a group with no pages, or a date window
// that excludes everything, still returns a tidy result — just the wrong one.
// Opening the real search is the only way to see what was actually asked for.

interface SearchUrlPreviewProps {
  url: string | null;
  loading: boolean;
  advertiserCount: number;
}

export default function SearchUrlPreview({ url, loading, advertiserCount }: SearchUrlPreviewProps) {
  if (loading && !url) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.disabled' }}>
        <CircularProgress size={14} />
        <Typography variant="caption">Building the Ad Library link…</Typography>
      </Stack>
    );
  }

  if (!url) return null;

  return (
    <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        <Link href={url} target="_blank" rel="noreferrer" variant="body2">
          Preview this search in the Ad Library
        </Link>
        {loading && <CircularProgress size={12} />}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {/* Stated because it is the thing most likely to be wrong and least
            visible: a group whose pages were removed filters nothing, and an
            unfiltered search looks identical to a working one. */}
        {advertiserCount
          ? `Filtered to ${advertiserCount} advertiser page${advertiserCount === 1 ? '' : 's'}`
          : 'No advertiser filter — this searches every advertiser'}
      </Typography>
    </Alert>
  );
}
